import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowLeft, Package, MapPin, Wrench, Calendar, Download, Shield,
  AlertTriangle, FileText, Clock, DollarSign, Activity, Tag, ArrowRightLeft, Wifi,
} from 'lucide-react'
import { useAuthStore } from '@/store/auth'
import { DocumentPanel } from '@/components/DocumentPanel'
import NfcTagProgrammer from '@/components/NfcTagProgrammer'
import { AssetTransferModal } from '@/components/AssetTransferModal'
import { apiGet } from '@/lib/api'
import {
  cn, formatDate, formatDateTime, formatCurrency, isOverdue,
  PRIORITY_COLORS, PRIORITY_LABELS, STATUS_LABELS,
} from '@/lib/utils'
import { useState } from 'react'

// ── Types ───────────────────────────────────────────────────────────────────
interface AssetDetail {
  id: string
  name: string
  code: string
  description: string | null
  status: string
  criticality: string
  make: string | null
  model: string | null
  serialNumber: string | null
  assetTag: string | null
  installDate: string | null
  warrantyExpiry: string | null
  expectedLifeYears: number | null
  replacementCostGbp: number | null
  purchaseCostGbp: number | null
  purchaseDate: string | null
  supplier: string | null
  invoiceNo: string | null
  deliveryDate: string | null
  subCategory: string | null
  condition: string | null
  attributes: Record<string, unknown>
  notes: string | null
  nfcUid: string | null
  nfcProgrammedAt: string | null
  trackMovement: boolean
  createdAt: string
  category: { id: string; name: string; code: string } | null
  building: { id: string; name: string; code: string } | null
  floor: { id: string; name: string; number: number } | null
  zone: { id: string; name: string } | null
  room: { id: string; name: string; number: string } | null
  documents: AssetDocument[]
  qrTags: { id: string; tagCode: string; isActive: boolean }[]
  ppmSchedules: PPMSchedule[]
  sensorPoints: SensorPoint[]
  maintenanceCosts: MaintenanceCost[]
  workOrders: WorkOrderSummary[]
  stats: {
    totalMaintenanceCostGbp: number
    workOrderCount: number
    activePpmCount: number
  }
}

interface AssetDocument {
  id: string
  fileName: string
  fileType: string
  fileSize: number
  category: string
  url: string
  uploadedAt: string
}

interface PPMSchedule {
  id: string
  title: string
  frequency: string
  nextDueDate: string | null
  isCompliance: boolean
}

interface SensorPoint {
  id: string
  name: string
  unit: string
  dataType: string
  isAlarmPoint: boolean
}

interface MaintenanceCost {
  id: string
  description: string
  amountGbp: number
  type: string
  incurredAt: string
}

interface WorkOrderSummary {
  id: string
  woNumber: string
  title: string
  status: string
  priority: string
  createdAt: string
  completedAt: string | null
}

interface AssetMovement {
  id: string
  movementType: string
  fromProperty: { name: string } | null
  toProperty: { name: string } | null
  checkedOutTo: string | null
  scannedByUser: { firstName: string; lastName: string }
  notes: string | null
  scannedAt: string
}

const MOVEMENT_LABELS: Record<string, string> = {
  DISPATCH: 'Dispatched',
  ARRIVAL: 'Arrived',
  AUDIT: 'Audit Scan',
  CHECKOUT: 'Checked Out',
  CHECKIN: 'Checked In',
  MAINTENANCE_OUT: 'Sent for Maintenance',
  MAINTENANCE_IN: 'Returned from Maintenance',
}

const MOVEMENT_COLORS: Record<string, string> = {
  DISPATCH: 'bg-orange-100 text-orange-800',
  ARRIVAL: 'bg-green-100 text-green-800',
  AUDIT: 'bg-gray-100 text-gray-700',
  CHECKOUT: 'bg-blue-100 text-blue-800',
  CHECKIN: 'bg-blue-100 text-blue-800',
  MAINTENANCE_OUT: 'bg-yellow-100 text-yellow-800',
  MAINTENANCE_IN: 'bg-green-100 text-green-800',
}

const CRITICALITY_COLORS: Record<string, string> = {
  CRITICAL: 'bg-red-100 text-red-800',
  HIGH: 'bg-amber-100 text-amber-800',
  MEDIUM: 'bg-blue-100 text-blue-800',
  LOW: 'bg-gray-100 text-gray-700',
}

const ASSET_STATUS_COLORS: Record<string, string> = {
  ACTIVE: 'bg-green-100 text-green-800',
  UNDER_REPAIR: 'bg-yellow-100 text-yellow-800',
  DECOMMISSIONED: 'bg-red-50 text-red-600',
  AWAITING_COMMISSIONING: 'bg-blue-100 text-blue-800',
  DISPOSED: 'bg-gray-100 text-gray-500',
}

const WO_STATUS_COLORS: Record<string, string> = {
  NEW: 'bg-gray-100 text-gray-700',
  ASSIGNED: 'bg-blue-100 text-blue-800',
  IN_PROGRESS: 'bg-yellow-100 text-yellow-800',
  ON_HOLD: 'bg-orange-100 text-orange-800',
  COMPLETED: 'bg-green-100 text-green-800',
  VERIFIED: 'bg-emerald-100 text-emerald-800',
  CANCELLED: 'bg-red-50 text-red-600',
}

export function AssetDetailPage() {
  const { assetId } = useParams<{ assetId: string }>()
  const navigate = useNavigate()
  const { currentPropertyId } = useAuthStore()
  const pid = currentPropertyId

  const [activeTab, setActiveTab] = useState<'workorders' | 'ppm' | 'costs' | 'sensors' | 'movements'>('workorders')
  const [showTransfer, setShowTransfer] = useState(false)

  const { data: asset, isLoading, error } = useQuery({
    queryKey: ['asset', assetId],
    queryFn: () => apiGet<AssetDetail>(`/properties/${pid}/assets/${assetId}`),
    enabled: !!pid && !!assetId,
  })

  const { data: movements } = useQuery({
    queryKey: ['asset-movements', assetId],
    queryFn: () => apiGet<AssetMovement[]>(`/assets/nfc/assets/${assetId}/movements`),
    enabled: !!assetId && activeTab === 'movements',
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy-800" />
      </div>
    )
  }

  if (error || !asset) {
    return (
      <div className="text-center py-16">
        <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-lg font-semibold text-navy-800">Asset not found</h2>
        <button onClick={() => navigate('/assets')} className="btn-secondary mt-4">
          Back to Assets
        </button>
      </div>
    )
  }

  const warrantyExpired = asset.warrantyExpiry && isOverdue(asset.warrantyExpiry)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <button onClick={() => navigate('/assets')} className="mt-1 text-gray-400 hover:text-navy-800">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-gray-400 font-mono">{asset.code}</span>
              <span className={cn('badge', CRITICALITY_COLORS[asset.criticality])}>{asset.criticality}</span>
              <span className={cn('badge', ASSET_STATUS_COLORS[asset.status])}>
                {asset.status.replace(/_/g, ' ')}
              </span>
              {asset.category && (
                <span className="badge bg-navy-100 text-navy-800">{asset.category.name}</span>
              )}
            </div>
            <h1 className="text-xl font-bold text-navy-800 mt-1">{asset.name}</h1>
            {asset.make && (
              <p className="text-sm text-gray-400 mt-0.5">
                {asset.make}{asset.model ? ` ${asset.model}` : ''}
                {asset.serialNumber ? ` \u2022 S/N: ${asset.serialNumber}` : ''}
              </p>
            )}
          </div>
        </div>

        <div className="flex gap-2 flex-shrink-0">
          <button
            onClick={() => setShowTransfer(true)}
            className="btn-primary flex items-center gap-2"
          >
            <ArrowRightLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Transfer</span>
          </button>
          <button
            onClick={() => window.open(`/api/v1/properties/${pid}/assets/${assetId}/qr`, '_blank')}
            className="btn-secondary"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">QR Code</span>
          </button>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="card py-4">
          <p className="text-xs text-gray-400">Work Orders</p>
          <p className="text-2xl font-bold text-navy-800">{asset.stats.workOrderCount}</p>
        </div>
        <div className="card py-4">
          <p className="text-xs text-gray-400">Active PPM</p>
          <p className="text-2xl font-bold text-navy-800">{asset.stats.activePpmCount}</p>
        </div>
        <div className="card py-4">
          <p className="text-xs text-gray-400">Maintenance Cost</p>
          <p className="text-2xl font-bold text-navy-800">{formatCurrency(asset.stats.totalMaintenanceCostGbp)}</p>
        </div>
        <div className="card py-4">
          <p className="text-xs text-gray-400">Replacement Value</p>
          <p className="text-2xl font-bold text-navy-800">{formatCurrency(asset.replacementCostGbp)}</p>
        </div>
      </div>

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left — tabs */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          {asset.description && (
            <div className="card">
              <h2 className="text-sm font-semibold text-navy-800 mb-2">Description</h2>
              <p className="text-sm text-gray-600 whitespace-pre-wrap">{asset.description}</p>
            </div>
          )}

          {/* Tabs */}
          <div className="card p-0">
            <div className="flex border-b border-gray-100">
              {([
                { key: 'workorders' as const, label: 'Work Orders', icon: Wrench, count: asset.workOrders.length },
                { key: 'ppm' as const, label: 'PPM Schedules', icon: Calendar, count: asset.ppmSchedules.length },
                { key: 'costs' as const, label: 'Costs', icon: DollarSign, count: asset.maintenanceCosts.length },
                { key: 'sensors' as const, label: 'Sensors', icon: Activity, count: asset.sensorPoints.length },
                { key: 'movements' as const, label: 'Movements', icon: ArrowRightLeft, count: movements?.length ?? 0 },
              ]).map(({ key, label, icon: Icon, count }) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className={cn(
                    'flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors',
                    activeTab === key
                      ? 'border-navy-800 text-navy-800'
                      : 'border-transparent text-gray-400 hover:text-gray-600'
                  )}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                  {count > 0 && (
                    <span className="bg-gray-100 text-gray-600 rounded-full px-1.5 py-0.5 text-xs">{count}</span>
                  )}
                </button>
              ))}
            </div>

            <div className="p-6">
              {/* Work Orders tab */}
              {activeTab === 'workorders' && (
                <div className="space-y-3">
                  {asset.workOrders.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-4">No work orders for this asset</p>
                  ) : (
                    asset.workOrders.map((wo) => (
                      <Link
                        key={wo.id}
                        to={`/work-orders/${wo.id}`}
                        className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className={cn('badge', PRIORITY_COLORS[wo.priority])}>
                              {PRIORITY_LABELS[wo.priority]}
                            </span>
                            <span className={cn('badge', WO_STATUS_COLORS[wo.status])}>
                              {STATUS_LABELS[wo.status] ?? wo.status}
                            </span>
                            <span className="text-xs text-gray-400 font-mono">{wo.woNumber}</span>
                          </div>
                          <p className="text-sm font-medium text-navy-800 truncate">{wo.title}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-xs text-gray-400">{formatDate(wo.createdAt)}</p>
                          {wo.completedAt && (
                            <p className="text-xs text-green-600">Completed {formatDate(wo.completedAt)}</p>
                          )}
                        </div>
                      </Link>
                    ))
                  )}
                </div>
              )}

              {/* PPM tab */}
              {activeTab === 'ppm' && (
                <div className="space-y-3">
                  {asset.ppmSchedules.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-4">No PPM schedules for this asset</p>
                  ) : (
                    asset.ppmSchedules.map((ppm) => (
                      <div key={ppm.id} className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            {ppm.isCompliance && (
                              <span className="badge bg-purple-100 text-purple-800">Compliance</span>
                            )}
                            <span className="badge bg-gray-100 text-gray-600">{ppm.frequency}</span>
                          </div>
                          <p className="text-sm font-medium text-navy-800">{ppm.title}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-xs text-gray-400">Next Due</p>
                          <p className={cn('text-xs font-medium',
                            ppm.nextDueDate && isOverdue(ppm.nextDueDate) ? 'text-red-600' : 'text-gray-600'
                          )}>
                            {formatDate(ppm.nextDueDate)}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Costs tab */}
              {activeTab === 'costs' && (
                <div className="space-y-3">
                  {asset.maintenanceCosts.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-4">No maintenance costs recorded</p>
                  ) : (
                    <>
                      <div className="bg-gray-50 rounded-lg p-4 mb-4">
                        <p className="text-xs text-gray-400">Total Maintenance Cost</p>
                        <p className="text-2xl font-bold text-navy-800">
                          {formatCurrency(asset.stats.totalMaintenanceCostGbp)}
                        </p>
                      </div>
                      {asset.maintenanceCosts.map((c) => (
                        <div key={c.id} className="flex justify-between items-center py-2 border-b border-gray-50 last:border-0">
                          <div>
                            <p className="text-sm text-navy-800">{c.description}</p>
                            <p className="text-xs text-gray-400">{formatDate(c.incurredAt)} \u2022 {c.type}</p>
                          </div>
                          <span className="text-sm font-medium text-navy-800">{formatCurrency(c.amountGbp)}</span>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}

              {/* Sensors tab */}
              {activeTab === 'sensors' && (
                <div className="space-y-3">
                  {asset.sensorPoints.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-4">No sensors connected to this asset</p>
                  ) : (
                    asset.sensorPoints.map((sp) => (
                      <div key={sp.id} className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50">
                        <Activity className={cn('w-5 h-5 flex-shrink-0', sp.isAlarmPoint ? 'text-red-500' : 'text-gray-400')} />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-navy-800">{sp.name}</p>
                          <p className="text-xs text-gray-400">{sp.dataType} \u2022 {sp.unit}</p>
                        </div>
                        {sp.isAlarmPoint && (
                          <span className="badge bg-red-100 text-red-800">Alarm</span>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Movements tab */}
              {activeTab === 'movements' && (
                <div className="space-y-3">
                  {!movements || movements.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-4">No movement history for this asset</p>
                  ) : (
                    movements.map((m) => (
                      <div key={m.id} className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50">
                        <ArrowRightLeft className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className={cn('badge', MOVEMENT_COLORS[m.movementType] ?? 'bg-gray-100 text-gray-700')}>
                              {MOVEMENT_LABELS[m.movementType] ?? m.movementType}
                            </span>
                          </div>
                          <div className="text-sm text-navy-800">
                            {m.fromProperty && <span>From {m.fromProperty.name}</span>}
                            {m.fromProperty && m.toProperty && <span> → </span>}
                            {m.toProperty && <span>To {m.toProperty.name}</span>}
                            {m.checkedOutTo && <span className="text-gray-500"> — {m.checkedOutTo}</span>}
                          </div>
                          {m.notes && <p className="text-xs text-gray-500 mt-1">{m.notes}</p>}
                          <p className="text-xs text-gray-400 mt-1">
                            {m.scannedByUser.firstName} {m.scannedByUser.lastName} — {formatDateTime(m.scannedAt)}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right sidebar */}
        <div className="space-y-6">
          {/* Location */}
          <div className="card">
            <h2 className="text-sm font-semibold text-navy-800 mb-3 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-gray-400" /> Location
            </h2>
            <div className="space-y-2 text-sm">
              {asset.building && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Building</span>
                  <span className="text-navy-800">{asset.building.name}</span>
                </div>
              )}
              {asset.floor && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Floor</span>
                  <span className="text-navy-800">{asset.floor.name}</span>
                </div>
              )}
              {asset.zone && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Zone</span>
                  <span className="text-navy-800">{asset.zone.name}</span>
                </div>
              )}
              {asset.room && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Room</span>
                  <span className="text-navy-800">{asset.room.number} - {asset.room.name}</span>
                </div>
              )}
              {!asset.building && !asset.room && (
                <p className="text-gray-400">No location set</p>
              )}
            </div>
          </div>

          {/* Lifecycle */}
          <div className="card">
            <h2 className="text-sm font-semibold text-navy-800 mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4 text-gray-400" /> Lifecycle
            </h2>
            <div className="space-y-2 text-sm">
              {asset.installDate && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Installed</span>
                  <span className="text-navy-800">{formatDate(asset.installDate)}</span>
                </div>
              )}
              {asset.purchaseDate && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Purchased</span>
                  <span className="text-navy-800">{formatDate(asset.purchaseDate)}</span>
                </div>
              )}
              {asset.purchaseCostGbp != null && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Purchase Cost</span>
                  <span className="text-navy-800 font-semibold">{formatCurrency(asset.purchaseCostGbp)}</span>
                </div>
              )}
              {asset.supplier && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Supplier</span>
                  <span className="text-navy-800">{asset.supplier}</span>
                </div>
              )}
              {asset.invoiceNo && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Invoice</span>
                  <span className="text-navy-800 font-mono text-xs">{asset.invoiceNo}</span>
                </div>
              )}
              {asset.deliveryDate && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Delivered</span>
                  <span className="text-navy-800">{formatDate(asset.deliveryDate)}</span>
                </div>
              )}
              {asset.condition && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Condition</span>
                  <span className={cn('font-medium',
                    asset.condition === 'Excellent' ? 'text-green-600' :
                    asset.condition === 'Good' ? 'text-blue-600' :
                    asset.condition === 'Fair' ? 'text-amber-600' : 'text-red-600'
                  )}>{asset.condition}</span>
                </div>
              )}
              {asset.subCategory && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Sub-Category</span>
                  <span className="text-navy-800">{asset.subCategory}</span>
                </div>
              )}
              {asset.warrantyExpiry && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Warranty</span>
                  <span className={cn('font-medium', warrantyExpired ? 'text-red-600' : 'text-green-600')}>
                    {warrantyExpired ? 'Expired' : 'Active'} \u2022 {formatDate(asset.warrantyExpiry)}
                  </span>
                </div>
              )}
              {asset.expectedLifeYears && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Expected Life</span>
                  <span className="text-navy-800">{asset.expectedLifeYears} years</span>
                </div>
              )}
            </div>
          </div>

          {/* Identification */}
          <div className="card">
            <h2 className="text-sm font-semibold text-navy-800 mb-3 flex items-center gap-2">
              <Tag className="w-4 h-4 text-gray-400" /> Identification
            </h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Code</span>
                <span className="text-navy-800 font-mono">{asset.code}</span>
              </div>
              {asset.serialNumber && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Serial No.</span>
                  <span className="text-navy-800 font-mono">{asset.serialNumber}</span>
                </div>
              )}
              {asset.assetTag && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Asset Tag</span>
                  <span className="text-navy-800 font-mono">{asset.assetTag}</span>
                </div>
              )}
              {asset.make && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Make</span>
                  <span className="text-navy-800">{asset.make}</span>
                </div>
              )}
              {asset.model && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Model</span>
                  <span className="text-navy-800">{asset.model}</span>
                </div>
              )}
            </div>
          </div>

          {/* Documents */}
          {asset.documents.length > 0 && (
            <div className="card">
              <h2 className="text-sm font-semibold text-navy-800 mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4 text-gray-400" /> Documents
              </h2>
              <div className="space-y-2">
                {asset.documents.map((doc) => (
                  <a
                    key={doc.id}
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-2 -mx-2 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-navy-800 truncate">{doc.fileName}</p>
                      <p className="text-xs text-gray-400">{doc.category} \u2022 {formatDate(doc.uploadedAt)}</p>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* NFC / QR Tag */}
          <div className="card">
            <h2 className="text-sm font-semibold text-navy-800 mb-3 flex items-center gap-2">
              <Wifi className="w-4 h-4 text-gray-400" /> Tags
            </h2>
            <NfcTagProgrammer
              assetId={asset.id}
              assetName={asset.name}
              existingNfcUid={asset.nfcUid}
              existingQrCode={asset.qrTags?.[0]?.tagCode}
            />
          </div>

          {/* DocFlow Documents */}
          <DocumentPanel
            entityType="asset"
            entityId={asset.id}
            title="Documents"
            compact
          />

          {/* Notes */}
          {asset.notes && (
            <div className="card">
              <h2 className="text-sm font-semibold text-navy-800 mb-2">Notes</h2>
              <p className="text-sm text-gray-600 whitespace-pre-wrap">{asset.notes}</p>
            </div>
          )}
        </div>
      </div>

      {/* Transfer modal */}
      {showTransfer && asset && (
        <AssetTransferModal
          assetId={asset.id}
          assetName={asset.name}
          currentPropertyId={pid!}
          currentPropertyName={asset.building?.name ?? 'Unknown'}
          currentLocation={[
            asset.building?.name,
            asset.floor?.name,
            asset.room ? `Rm ${asset.room.number}` : null,
          ].filter(Boolean).join(' → ') || 'No location set'}
          onClose={() => setShowTransfer(false)}
          onSuccess={() => setActiveTab('movements')}
        />
      )}
    </div>
  )
}
